import { View, StyleSheet, Keyboard } from 'react-native';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import { IconBoldArrow } from 'design-system/icons';
import DepositAmountSelector from 'design-system/DepositAmountSelector';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { AnimatedKeyboardAwareScrollView } from 'design-system/AnimatedComponents';
import { rem, AppView, withAnimatedNavigationHeader } from 'design-system';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { formatAmount } from 'utils';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectTradingAccountFromId } from 'features/invest/reducers/selectors';
import { POT_DEPOSIT_AMOUNT_OPTIONS } from 'features/pots/constants';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { selectConnectedAccountBalanceFromConnectedAccountWithBankId, selectConnectedAccountWithBankId, useCurrentPrice, } from '../hooks/selectors';
import useHandleBuyAssetForTradingAccount from '../hooks/useHandleBuyAssetForTradingAccount';
import BuyAssetRow from '../components/BuyAssetRow';
import CashRow from '../components/CashRow';
import ConnectedAccountRow from '../components/ConnectedAccountRow';
import { TradingAccountDepositMethodPickerModal } from '../components/TradingAccountDepositMethodPickerModal';
import useOrderPreview from '../hooks/useOrderPreview';
import { getClosestDecimalCeilBias } from '../utils';
import { INVEST_MAXIMUM_DD_DEPOSIT_LIMIT, INVEST_MAXIMUM_OB_DEPOSIT_LIMIT } from '../constants';
import { isWeb } from '../../../constants';
import { blockInvestActionOnWeb } from '../webGate';
const strings = {
    title: 'Buy',
    viewOrder: 'View order',
    exceedBalanceError: 'Your balance is too low',
    belowOneErrorPrefix: 'Please enter amount more than ',
    minAmountErrorPrefix: 'Minimum amount is ',
    maxAmountErrorSuffix: ' maximum',
    leftToInvest: ' left to invest this tax year',
};
const BuyAssetForTradingAccountScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const { assetId, tradingAccountId } = route.params;
    const currentPrice = useCurrentPrice(assetId);
    const { connectedAccount: from } = useAppSelector(selectConnectedAccountWithBankId);
    const tradingAccount = useAppSelector((state) => selectTradingAccountFromId(state, tradingAccountId));
    const minimumTransferUnit = useAppSelector((store) => store.invest.caches[assetId]?.minimumTransferUnit);
    const assetType = useAppSelector((store) => store.invest.caches[assetId]?.type);
    const remainingAllowance = useMemo(() => {
        if (!tradingAccount)
            return undefined;
        // eslint-disable-next-line no-nested-ternary
        return tradingAccount?.accountType === 'JISA'
            ? tradingAccount?.jisaRemainingAllowanceAfterPending
            : tradingAccount?.accountType === 'ISA'
                ? tradingAccount?.isaRemainingAllowanceAfterPending
                : null;
    }, [tradingAccount]);
    const [isFromPickerOpen, setIsFromPickerOpen] = useState(false);
    const [amountError, setAmountError] = useState();
    // undefined means cash deposit. On web bank deposits are mobile-only, so it starts on cash
    const [depositMethod, setDepositMethod] = useState(
    // eslint-disable-next-line no-nested-ternary
    isWeb || (remainingAllowance && remainingAllowance.value <= 0)
        ? undefined
        : from?.pispBankId
            ? 'openBankingDeposit'
            : 'singleDirectDebit');
    const maxDepositLimit = depositMethod === 'singleDirectDebit' ? INVEST_MAXIMUM_DD_DEPOSIT_LIMIT : INVEST_MAXIMUM_OB_DEPOSIT_LIMIT;
    const connectedAccountBalance = useAppSelector(selectConnectedAccountBalanceFromConnectedAccountWithBankId);
    const [amount, setAmount] = useState(() => {
        if (connectedAccountBalance && depositMethod === 'openBankingDeposit') {
            if (connectedAccountBalance.amount >= 100) {
                return 100;
            }
            if (connectedAccountBalance.amount >= 50) {
                return 50;
            }
            return 0;
        }
        return 100;
    });
    const [fullOrderPreview, getOrderPreview, loading] = useOrderPreview(assetId, tradingAccountId, {
        operation: 'buy',
        updateOnMount: true,
        initialAmount: 0,
        fundOrderWithOpenBanking: depositMethod === 'openBankingDeposit',
        fundOrderWithDirectDebit: depositMethod === 'singleDirectDebit',
    });
    const latestPriceAmount = fullOrderPreview?.order?.estimated.price?.value || currentPrice?.value;
    const min = 
    // eslint-disable-next-line no-nested-ternary
    assetType === 'ETF'
        ? 0.0001
        : minimumTransferUnit && latestPriceAmount
            ? getClosestDecimalCeilBias(minimumTransferUnit * 1.1 * latestPriceAmount)
            : 1;
    const fromBalance = useMemo(() => {
        if (depositMethod === undefined) {
            if (tradingAccount?.availableToInvest) {
                return {
                    amount: tradingAccount.availableToInvest.value,
                    currency: tradingAccount.availableToInvest.currency,
                };
            }
            return undefined;
        }
        if (from && 'ukAccountNumber' in from && depositMethod === 'openBankingDeposit') {
            return connectedAccountBalance;
        }
        return undefined;
    }, [connectedAccountBalance, depositMethod, from, tradingAccount?.availableToInvest]);
    const colors = useColors();
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        navigation.setOptions({
            title: strings.title,
            headerTintColor: colors.elements.primary,
            headerRight: () => <HelpScreenButton />,
        });
    }, [colors.elements.primary, navigation]);
    const onFromPress = useCallback(() => {
        Keyboard.dismiss();
        setIsFromPickerOpen(true);
        if (from) {
            TradingAccountDepositMethodPickerModal.show((method) => {
                setDepositMethod(method);
                setIsFromPickerOpen(false);
            }, depositMethod, () => setIsFromPickerOpen(false), tradingAccountId, true);
        }
    }, [depositMethod, from, tradingAccountId]);
    const From = useMemo(() => {
        if (!depositMethod) {
            return (<CashRow tradingAccountId={tradingAccountId} onPress={from ? onFromPress : undefined} isPickerModalOpen={isFromPickerOpen}/>);
        }
        if (from) {
            return (<ConnectedAccountRow account={from} onPress={onFromPress} isPickerModalOpen={isFromPickerOpen} description={depositMethod === 'openBankingDeposit' ? 'Open Banking transfer' : 'Direct Debit'}/>);
        }
        return null;
    }, [depositMethod, from, isFromPickerOpen, onFromPress, tradingAccountId]);
    const To = useMemo(() => {
        if (assetId) {
            return <BuyAssetRow assetId={assetId} sharesEstimated={fullOrderPreview?.order.estimated.quantity || 0}/>;
        }
        return null;
    }, [assetId, fullOrderPreview?.order.estimated.quantity]);
    const checkAmountError = useCallback((value) => {
        if (value < 1) {
            setAmountError('belowOne');
        }
        else if (value < min) {
            setAmountError('belowMin');
        }
        else if (value > maxDepositLimit) {
            setAmountError('exceedMax');
        }
        else if (remainingAllowance != null && depositMethod !== undefined && value > remainingAllowance.value) {
            setAmountError('exceedAllowance');
        }
        else if (fromBalance && value > fromBalance.amount) {
            setAmountError('exceedBalance');
        }
        else {
            setAmountError(undefined);
        }
    }, [min, maxDepositLimit, remainingAllowance, depositMethod, fromBalance]);
    useEffect(() => {
        if (amount) {
            checkAmountError(amount);
        }
        else {
            setAmountError(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkAmountError]); // we want this hook to run only if checkAmountError updates
    useEffect(() => {
        if (!amountError) {
            getOrderPreview(amount || 0);
        }
    }, [getOrderPreview, amount, amountError]);
    const footnote = useMemo(() => {
        const isError = !!amountError;
        const colorProps = isError ? { Red: true } : { Secondary: true };
        let content;
        if (remainingAllowance && depositMethod !== undefined) {
            content = (<>
              <Text NumbersThin-14 {...colorProps}>
                {formatAmount(remainingAllowance.value, remainingAllowance.currency, 2)}
              </Text>
              {strings.leftToInvest}
            </>);
        }
        if (amountError === 'belowOne') {
            content = (<>
              {strings.belowOneErrorPrefix}
              <Text NumbersThin-14 Red>
                £1
              </Text>
            </>);
        }
        else if (amountError === 'belowMin') {
            content = (<>
              {strings.minAmountErrorPrefix}
              <Text NumbersThin-14 Red>
                {formatAmount(min, 'GBP')}
              </Text>
            </>);
        }
        else if (amountError === 'exceedMax') {
            content = (<>
              <Text NumbersThin-14 Red>
                {formatAmount(maxDepositLimit, 'GBP')}
              </Text>
              {strings.maxAmountErrorSuffix}
            </>);
        }
        else if (amountError === 'exceedBalance') {
            content = strings.exceedBalanceError;
        }
        if (!content)
            return undefined;
        return (<Text TextThin-14 {...colorProps}>
            {content}
          </Text>);
    }, [amountError, depositMethod, maxDepositLimit, min, remainingAllowance]);
    const handleAmountChange = useCallback((value) => {
        setAmount(value);
        checkAmountError(value);
    }, [checkAmountError]);
    const handleDepositAndBuy = useHandleBuyAssetForTradingAccount();
    const handleContinue = useCallback((skipMandateStatusCheck) => {
        Keyboard.dismiss();
        if (depositMethod !== undefined && blockInvestActionOnWeb('Add money'))
            return;
        if (depositMethod === 'singleDirectDebit' &&
            !skipMandateStatusCheck &&
            (from?.investMandateStatus === 'INACTIVE' || from?.investMandateStatus === 'NONE')) {
            navigation.navigate('AutoinvestSelectFundingAccount', {
                onDirectDebitSetup: () => {
                    handleContinue(true);
                },
            });
            return;
        }
        if (tradingAccount && from && amount) {
            navigation.navigate('SecclOrderPreviewScreen', {
                assetId,
                tradingAccountId,
                depositMethod,
                orderPreview: fullOrderPreview,
                accountType: tradingAccount.accountType,
                orderType: { type: 'buy', amount: { value: amount, currency: 'GBP' } },
                onConfirm: () => handleDepositAndBuy({ assetId, tradingAccountId, amount, depositMethod }),
            });
        }
    }, [amount, assetId, depositMethod, from, fullOrderPreview, handleDepositAndBuy, tradingAccount, tradingAccountId]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle, styles.contentContainer);
    return (<AppView withFrame={false}>
          <AnimatedKeyboardAwareScrollView enableOnAndroid onScroll={scrollHandler} scrollEventThrottle={16} keyboardShouldPersistTaps="handled" contentContainerStyle={contentContainerStyle} showsVerticalScrollIndicator={false}>
            {From}
            <Spacer h={16}/>
            <View>
              {To}
              <View style={styles.iconContainer} pointerEvents="none">
                <View style={styles.iconWrapper}>
                  <IconBoldArrow alt colorKey="brand" direction="bottom"/>
                </View>
              </View>
            </View>
            <Spacer h={16}/>
            <DepositAmountSelector amount={amount} footnote={footnote} max={maxDepositLimit} min={Math.max(min, 1)} setAmount={handleAmountChange} options={POT_DEPOSIT_AMOUNT_OPTIONS} availableAmount={fromBalance?.amount} remainingAllowance={depositMethod !== undefined ? remainingAllowance?.value : undefined}/>
          </AnimatedKeyboardAwareScrollView>
          <KeyboardAvoidingButton brand isFetching={loading} title={strings.viewOrder} onPress={() => handleContinue()} disabled={!tradingAccount || !from || !amount || !!amountError || !fullOrderPreview?.order}/>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
    title: strings.title,
}));
const styleSet = createStyleSheets((colors) => ({
    iconContainer: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
        top: -rem(30),
    },
    iconWrapper: {
        width: rem(42),
        height: rem(42),
        borderWidth: rem(1),
        alignItems: 'center',
        borderRadius: rem(44),
        justifyContent: 'center',
        borderColor: colors.borders.logoStroke,
        backgroundColor: colors.background.light,
    },
    contentContainer: {
        paddingTop: rem(16),
    },
}));
export default BuyAssetForTradingAccountScreen;
